import { Ban, Check, Circle, Eye, LoaderCircle, X } from 'lucide-react';
import type { ComponentType } from 'react';
import { ActivityStatus, Priority, ProjectHealth, type RiskSeverity } from '@kainos/shared';
import { Badge } from '../badge/Badge.js';
import {
  ACTIVITY_STATUS_LABEL,
  PRIORITY_LABEL,
  PROJECT_HEALTH_LABEL,
  RISK_SEVERITY_LABEL,
} from '../labels/index.js';
import {
  ACTIVITY_STATUS_TONE,
  PRIORITY_TONE,
  PROJECT_HEALTH_TONE,
  RISK_SEVERITY_TONE,
} from './tone.js';

type IconComponent = ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;

const ACTIVITY_ICON: Record<ActivityStatus, IconComponent> = {
  [ActivityStatus.PENDING]: Circle,
  [ActivityStatus.IN_PROGRESS]: LoaderCircle,
  [ActivityStatus.BLOCKED]: Ban,
  [ActivityStatus.IN_REVIEW]: Eye,
  [ActivityStatus.DONE]: Check,
  [ActivityStatus.CANCELLED]: X,
};

const HEALTH_ICON: Record<ProjectHealth, IconComponent> = {
  [ProjectHealth.NORMAL]: Check,
  [ProjectHealth.ATTENTION]: Eye,
  [ProjectHealth.CRITICAL]: Ban,
};

const PRIORITY_ICON: Record<Priority, IconComponent> = {
  [Priority.LOW]: Circle,
  [Priority.MEDIUM]: LoaderCircle,
  [Priority.HIGH]: Eye,
  [Priority.CRITICAL]: Ban,
};

const ICON_SIZE = 13;
const ICON_STROKE = 2.3;

export interface StatusBadgeProps {
  status: ActivityStatus;
  className?: string;
}

/** Activity status pill — the most reused element in the product. */
export function StatusBadge({ status, className }: StatusBadgeProps) {
  const Icon = ACTIVITY_ICON[status];
  return (
    <Badge
      tone={ACTIVITY_STATUS_TONE[status]}
      icon={<Icon size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
      className={className}
    >
      {ACTIVITY_STATUS_LABEL[status]}
    </Badge>
  );
}

export interface PriorityBadgeProps {
  priority: Priority;
  className?: string;
}

export function PriorityBadge({ priority, className }: PriorityBadgeProps) {
  const Icon = PRIORITY_ICON[priority];
  return (
    <Badge
      tone={PRIORITY_TONE[priority]}
      icon={<Icon size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
      className={className}
    >
      {PRIORITY_LABEL[priority]}
    </Badge>
  );
}

export interface ProjectHealthBadgeProps {
  health: ProjectHealth;
  className?: string;
}

export function ProjectHealthBadge({ health, className }: ProjectHealthBadgeProps) {
  const Icon = HEALTH_ICON[health];
  return (
    <Badge
      tone={PROJECT_HEALTH_TONE[health]}
      icon={<Icon size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
      className={className}
    >
      {PROJECT_HEALTH_LABEL[health]}
    </Badge>
  );
}

export interface SeverityBadgeProps {
  severity: RiskSeverity;
  className?: string;
}

export function SeverityBadge({ severity, className }: SeverityBadgeProps) {
  return (
    <Badge tone={RISK_SEVERITY_TONE[severity]} className={className}>
      {RISK_SEVERITY_LABEL[severity]}
    </Badge>
  );
}
